import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { getRecognitionErrorMessage, useEuropeUsaImport } from '@/api/recognition';

const ERRORS_PER_PAGE = 100;

interface Props {
  csv: string;
  ou: string;
  importer: ReturnType<typeof useEuropeUsaImport>;
  onBack: () => void;
  onNext: () => void;
}

/** Paso 3 del wizard de Usuarios: vista previa de solo lectura (sin grid editable) + errores paginados. */
export function StepReview({ csv, ou, importer, onBack, onNext }: Readonly<Props>) {
  const { t } = useTranslation('recognition');
  const ranPreview = useRef(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    if (ranPreview.current) return;
    ranPreview.current = true;
    importer.mutate({ csv, preview: true, ou });
    // Solo se ejecuta una vez al entrar al paso.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const result = importer.data;
  const errors = result?.errors ?? [];
  const totalPages = Math.max(1, Math.ceil(errors.length / ERRORS_PER_PAGE));
  const pageErrors = errors.slice((page - 1) * ERRORS_PER_PAGE, page * ERRORS_PER_PAGE);

  const downloadErrors = () => {
    if (!result?.errorsCsv) return;
    const blob = new Blob(['﻿' + result.errorsCsv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const anchor = document.createElement('a');
    anchor.href = url;
    anchor.download = 'errores-padron.csv';
    anchor.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="bulk-step bulk-step--review">
      {importer.isPending && <p className="adm-status-loading" role="status">{t('common.loading')}</p>}

      {importer.isError && (
        <p className="rp-error" role="alert">
          <i className="fas fa-triangle-exclamation" />{' '}
          {getRecognitionErrorMessage(importer.error, t('admin.bulkUsers.importError'))}
        </p>
      )}

      {result?.disabled && (
        <p className="rp-error" role="alert">{t('admin.bulkUsers.disabled')}</p>
      )}

      {result && !result.disabled && (
        <>
          <p className="adm-note adm-note--strong">
            <i className="fas fa-eye" /> {t('admin.bulkUsers.previewNotice')}
          </p>

          <div className="adm-import-summary">
            <div className="adm-stat">
              <div className="adm-stat-num">{result.totalRows}</div>
              <div className="adm-stat-lbl">{t('admin.bulkUsers.received')}</div>
            </div>
            <div className="adm-stat">
              <div className="adm-stat-num adm-stat-num--created">{result.created}</div>
              <div className="adm-stat-lbl">{t('admin.bulkUsers.created')}</div>
            </div>
            <div className="adm-stat">
              <div className="adm-stat-num adm-stat-num--updated">{result.updated}</div>
              <div className="adm-stat-lbl">{t('admin.bulkUsers.updated')}</div>
            </div>
            <div className="adm-stat">
              <div className="adm-stat-num">{result.deactivated}</div>
              <div className="adm-stat-lbl">{t('admin.bulkUsers.deactivated')}</div>
            </div>
            <div className="adm-stat">
              <div className="adm-stat-num adm-stat-num--skipped">{result.skipped}</div>
              <div className="adm-stat-lbl">{t('admin.bulkUsers.skipped')}</div>
            </div>
          </div>

          {(result.ignoredColumns?.length ?? 0) > 0 && (
            <p className="adm-note adm-note--warn" role="alert">
              <i className="fas fa-triangle-exclamation" />{' '}
              {t('admin.bulkUsers.ignoredColumnsNotice', { columns: result.ignoredColumns!.join(', ') })}
            </p>
          )}

          {errors.length === 0 && (
            <p className="adm-note adm-note--ok">
              <i className="fas fa-circle-check" /> {t('admin.bulkUsers.allOk')}
            </p>
          )}

          {errors.length > 0 && (
            <>
              <div className="adm-toolbar">
                <button type="button" className="btn btn--secondary" onClick={downloadErrors}>
                  <i className="fas fa-download" /> {t('admin.bulkUsers.downloadErrors')}
                </button>
              </div>
              <div className="adm-table-wrap">
                <table className="adm-table">
                  <thead>
                    <tr>
                      <th>{t('bulk.colLine')}</th>
                      <th>{t('admin.bulkUsers.errorColumn')}</th>
                      <th>{t('admin.bulkUsers.errorDetail')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageErrors.map((rowError, index) => (
                      <tr key={`${rowError.lineNumber}-${rowError.column}-${index}`} className="adm-row--err">
                        <td>{rowError.lineNumber}</td>
                        <td>{rowError.column}</td>
                        <td>{rowError.message}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {totalPages > 1 && (
                <div className="adm-review-pagination">
                  <button type="button" className="btn btn--secondary" disabled={page === 1}
                    onClick={() => setPage((current) => current - 1)}>
                    {t('bulk.back')}
                  </button>
                  <span>{t('admin.bulkUsers.paginationPage', { page, total: totalPages })}</span>
                  <button type="button" className="btn btn--secondary" disabled={page === totalPages}
                    onClick={() => setPage((current) => current + 1)}>
                    {t('bulk.next')}
                  </button>
                </div>
              )}
            </>
          )}
        </>
      )}

      <div className="bulk-wizard-nav">
        <button type="button" className="btn btn--secondary" onClick={onBack}>
          <i className="fas fa-arrow-left" /> {t('bulk.back')}
        </button>
        <button type="button" className="btn btn--send" disabled={!result || result.disabled || importer.isPending}
          onClick={onNext}>
          {t('bulk.next')} <i className="fas fa-arrow-right" />
        </button>
      </div>
    </div>
  );
}

export default StepReview;
